import {CheckboxFillIcon} from '@primer/octicons-react'
import {Button, Tooltip} from '@primer/react'
import styles from './MarkAsViewedButton.module.css'
import responsiveStyles from './responsive.module.css'
import {clsx} from 'clsx'
import {useUpdateFileViewedStatusMutation} from '../mutations/use-update-file-viewed-status-mutation'
import {useDiffSummariesActions, useViewedFileStatus, useIsSingleFileMode} from '../stores/selectors'
import {useDiffNavigation} from '../hooks/use-diff-navigation'
import {useMemo, useCallback} from 'react'

export function MarkAsViewedButton({
  basePath,
  inactive = false,
  inactiveTooltip,
  path,
  setIsCollapsed,
}: {
  basePath: string
  inactive?: boolean
  inactiveTooltip?: string
  path: string
  setIsCollapsed: (collapsed: boolean) => void
}) {
  const isViewed = useViewedFileStatus(path)
  const {mutate: updateFileViewedStatus} = useUpdateFileViewedStatusMutation(basePath)
  const {setDiffViewed} = useDiffSummariesActions()
  const isInSingleFileMode = useIsSingleFileMode()
  const {navigateToNext} = useDiffNavigation()

  const handleClick = useCallback(() => {
    if (inactive) return

    updateFileViewedStatus({viewedStatus: !isViewed, path})
    setDiffViewed(path, !isViewed)
    if (isInSingleFileMode) {
      navigateToNext()
    } else {
      setIsCollapsed(!isViewed)
    }
  }, [
    updateFileViewedStatus,
    inactive,
    isViewed,
    path,
    setDiffViewed,
    isInSingleFileMode,
    navigateToNext,
    setIsCollapsed,
  ])

  const icon = useMemo(
    () =>
      isViewed ? (
        <CheckboxFillIcon className="color-fg-accent" />
      ) : (
        // This SVG is the square around CheckboxIcon without the Check (will not be shipped to Primer due to fear of misuse as a form element, which we don't do here, it's just visual)
        <svg
          // aria-pressed already informs that this is a toggle icon, so we're setting aria-hidden
          aria-hidden="true"
          fill="none"
          height="16"
          role="img"
          viewBox="0 0 16 16"
          width="16"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            clipRule="evenodd"
            d="M2.5 2.75C2.5 2.61193 2.61193 2.5 2.75 2.5H13.25C13.3881 2.5 13.5 2.61193 13.5 2.75V13.25C13.5 13.3881 13.3881 13.5 13.25 13.5H2.75C2.61193 13.5 2.5 13.3881 2.5 13.25V2.75ZM2.75 1C1.7835 1 1 1.7835 1 2.75V13.25C1 14.2165 1.7835 15 2.75 15H13.25C14.2165 15 15 14.2165 15 13.25V2.75C15 1.7835 14.2165 1 13.25 1H2.75Z"
            fill="currentColor"
            fillRule="evenodd"
          />
        </svg>
      ),
    [isViewed],
  )

  const button = (
    <Button
      aria-pressed={isViewed}
      aria-label={isViewed ? 'Viewed' : 'Not Viewed'}
      inactive={inactive}
      size="small"
      variant="default"
      leadingVisual={() => icon}
      className={clsx(isViewed && styles.viewed, styles.iconOnly)}
      onClick={handleClick}
    >
      <span className={responsiveStyles['hide-on-mobile']}>Viewed</span>
    </Button>
  )

  if (inactive && inactiveTooltip) {
    return <Tooltip text={inactiveTooltip}>{button}</Tooltip>
  } else {
    return button
  }
}
